import { formatFileSize } from '@/components/opportunity/common/transforms/formatters'

/** How much of a download has arrived, and how much there is to come. */
export interface DownloadProgress {
  loaded: number
  /** Bytes in all, when the response says; a chunked or gzipped one does not. */
  total: number | null
}

/** A report at most this often: a 30MB file arrives in hundreds of chunks. */
const REPORT_INTERVAL_MS = 100

/**
 * The body of a response as a Blob, reporting how much of it has come as it
 * comes. `Response.blob()` says nothing until it has everything, which for a
 * whole proposal is several seconds of a reader looking at a bar that cannot
 * move.
 *
 * A total larger than the bytes read is possible — Content-Length is the
 * encoded size, the stream is the decoded one — so what is reported is left
 * as read and the caller clamps. A body the platform will not stream is read
 * whole, and reports once.
 */
export const readBlobWithProgress = async (
  response: Response,
  onProgress: (progress: DownloadProgress) => void,
): Promise<Blob> => {
  const declared = Number(response.headers.get('content-length'))
  const total = Number.isFinite(declared) && declared > 0 ? declared : null
  const type = response.headers.get('content-type') ?? ''
  if (!response.body) {
    const blob = await response.blob()
    onProgress({ loaded: blob.size, total: total ?? blob.size })
    return blob
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array<ArrayBuffer>[] = []
  let loaded = 0
  let reportedAt = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    chunks.push(value)
    loaded += value.byteLength
    const now = performance.now()
    if (now - reportedAt >= REPORT_INTERVAL_MS) {
      reportedAt = now
      onProgress({ loaded, total })
    }
  }
  onProgress({ loaded, total: total ?? loaded })
  return new Blob(chunks, { type })
}

/** How much of the download has come, 0–1, when the whole is known. */
export const downloadFraction = ({
  loaded,
  total,
}: DownloadProgress): number | null =>
  total === null ? null : Math.min(1, loaded / total)

/**
 * "1.2 MB of 30.3 MB" — just what has come when the whole is unknown, and
 * nothing at all until something has.
 */
export const describeDownload = ({
  loaded,
  total,
}: DownloadProgress): string | null =>
  loaded === 0
    ? null
    : total === null
      ? formatFileSize(loaded)
      : `${formatFileSize(Math.min(loaded, total))} of ${formatFileSize(total)}`
